import React, { useState, useEffect } from 'react';
import { translations } from '../translations';
import apiService from '../services/apiService';

// Fonction de sanitisation pour prévenir XSS
const sanitizeInput = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .replace(/[<>]/g, '') // Supprime les balises HTML de base
    .replace(/javascript:/gi, '') // Supprime les protocoles javascript:
    .replace(/on\w+=/gi, '') // Supprime les gestionnaires d'événements (onclick, onload, etc.)
    .replace(/^\s+|\s+$/g, '') // Supprime seulement les espaces en début/fin (trim)
    .replace(/\s{2,}/g, ' ') // Remplace les espaces multiples par un seul espace
    .slice(0, 500); // Limite à 500 caractères
};

// Fonction de validation pour les champs texte
const validateTextInput = (input) => {
  if (!input) return true; // Champs optionnels
  
  // Vérifications de sécurité
  if (input.length > 500) return false;
  if (/<script|javascript:|data:/i.test(input)) return false;
  if (/[<>]/.test(input)) return false;
  
  return true;
};

// Composant YesNoQuestion défini à l'extérieur pour éviter les re-créations
const YesNoQuestion = ({ field, question, required = true, detailField = null, detailPlaceholder = null, responses, onResponseChange }) => {
  const handleRadioChange = (value) => {
    onResponseChange(field, value);
  };

  const handleTextChange = (e) => {
    const rawValue = e.target.value;
    
    // Validation simple : juste la longueur et pas de scripts dangereux
    if (rawValue.length > 500) {
      return; // Bloque seulement si trop long
    }
    if (/<script|javascript:|data:/i.test(rawValue)) {
      return; // Bloque seulement le contenu vraiment dangereux
    }
    
    // Passer la valeur directement sans sanitisation excessive
    onResponseChange(detailField, rawValue);
  };

  return (
    <div className="question-item">
      <h4 className="question-title">
        {question}
        {required && <span className="required">*</span>}
      </h4>
      <div className="question-options">
        <label className="option-label">
          <input
            type="radio"
            name={field}
            value="ja"
            checked={responses[field] === 'ja'}
            onChange={(e) => handleRadioChange(e.target.value)}
            className="option-input"
          />
          <span className="option-text">Ja</span>
        </label>
        <label className="option-label">
          <input
            type="radio"
            name={field}
            value="nein"
            checked={responses[field] === 'nein'}
            onChange={(e) => handleRadioChange(e.target.value)}
            className="option-input"
          />
          <span className="option-text">Nein</span>
        </label>
      </div>
              {detailField && (
          <div className={`conditional-input-container ${responses[field] === 'ja' ? 'show' : ''}`}>
            <input
              type="text"
              className="conditional-input"
              placeholder={detailPlaceholder}
              value={responses[detailField] || ''}
              onChange={handleTextChange}
              maxLength="500"
              autoComplete="off"
              spellCheck="false"
            />
          </div>
        )}
    </div>
  );
};

const InfectionQuestionnaire = ({ patientData, taskId, language = 'de', onComplete, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [responses, setResponses] = useState({
    // Teil A - Postoperative Infektionssymptome
    fever: '',
    hip_inflammation: '',
    increased_pain: '',
    wound_drainage: '',
    medical_examination: '',
    medical_examination_details: '',
    
    // Teil B - Andere Infekte
    diarrhea: '',
    respiratory_infection: '',
    urinary_infection: '',
    
    // Teil C - Medikamentenexposition
    antibiotics: '',
    antibiotics_details: '',
    ppi: '',
    ppi_details: '',
    probiotics: '',
    probiotics_details: '',
    hospital_stay: '',
    hospital_stay_reason: ''
  });

  const t = translations[language] || translations.en;

  useEffect(() => {
    const loadExistingResponses = async () => {
      if (!patientData || !patientData.patientId || !taskId) return;
      
      try {
        const response = await apiService.getQuestionnaireResponse(patientData.patientId, taskId);
        if (response.success && response.data) {
          const existingResponses = typeof response.data.responses === 'string' 
            ? JSON.parse(response.data.responses) 
            : response.data.responses;
          setResponses(prev => ({ ...prev, ...existingResponses }));
          setIsEditing(true);
          console.log('Réponses existantes chargées:', existingResponses);
        }
      } catch (error) {
        console.log('Aucune réponse existante trouvée pour le questionnaire Infection');
      }
    };

    loadExistingResponses();
  }, [patientData, taskId]);

  const handleResponseChange = React.useCallback((field, value) => {
    setResponses(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Calcul du taux de complétion
  const calculateCompletionRate = () => {
    // Liste de toutes les questions obligatoires (les questions avec réponses Ja/Nein)
    const requiredFields = [
      'fever', 'hip_inflammation', 'increased_pain', 'wound_drainage', 'medical_examination',
      'diarrhea', 'respiratory_infection', 'urinary_infection',
      'antibiotics', 'ppi', 'probiotics', 'hospital_stay'
    ];
    
    if (requiredFields.length === 0) return 0;
    
    const answeredFields = requiredFields.filter(field => 
      responses[field] && (responses[field] === 'ja' || responses[field] === 'nein')
    );
    
    return answeredFields.length / requiredFields.length;
  };

  // Vérification si le questionnaire est complet
  const isComplete = () => {
    return calculateCompletionRate() === 1;
  };

  const validateResponses = () => {
    return isComplete();
  };

  const handleSubmit = async () => {
    if (!validateResponses()) {
      setError(language === 'de' ? 'Bitte beantworten Sie alle Fragen' : 'Please answer all questions');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const responseData = {
        patientId: patientData.patientId,
        taskId: taskId,
        questionnaireType: 'INFECTION',
        responses: JSON.stringify(responses),
        score: 100,
        language: language,
        submissionDate: new Date().toISOString()
      };

      const response = await apiService.submitQuestionnaire(responseData);

      if (response.success) {
        onComplete && onComplete(responses);
      } else {
        setError(response.message || 'Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Erreur soumission questionnaire:', error);
      setError('Erreur lors de la soumission du questionnaire');
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content">
        <div className="questionnaire-header">
          <div className="questionnaire-title">
            <div className="task-icon">
              <img src="/images/questionnaire-icon.png?v=1" alt="Task icon" style={{ width: '20px', height: '20px' }} />
            </div>
            Postoperative Infektion & mikrobiomrelevante Exposition
          </div>
          <button className="questionnaire-close" onClick={onClose}>×</button>
        </div>

        <div className="questionnaire-body">
          <div className="questionnaire-instructions">
            <p><strong>Instruktion</strong></p>
            <p>Die folgenden Fragen beziehen sich auf Ihren Gesundheitszustand in den letzten 7 Tagen. Bitte beantworten Sie jede Frage, auch wenn Sie sich gesund fühlen.</p>
          </div>

          {/* Teil A - Postoperative Infektionssymptome */}
          <div className="questions-list">
            <h3 className="section-questionnaire active">Teil A – Postoperative Infektionssymptome</h3>
            
            <YesNoQuestion
              field="fever"
              question="1. Hatten Sie in den letzten 7 Tagen Fieber über 38°C?"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
            
            <YesNoQuestion
              field="hip_inflammation"
              question="2. War Ihre Hüfte (Operationsbereich) wärmer, röter oder geschwollen?"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
            
            <YesNoQuestion
              field="increased_pain"
              question="3. Hatten Sie neu aufgetretene Schmerzen in der operierten Hüfte, die stärker wurden?"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
            
            <YesNoQuestion
              field="wound_drainage"
              question="4. Gab es Flüssigkeitsaustritt/Wundsekretion aus der Operationsnarbe?"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
            
            <YesNoQuestion
              field="medical_examination"
              question="5. Mussten Sie sich in den letzten 7 Tagen erneut einer Untersuchung oder Behandlung wegen Infektionsverdachts unterziehen?"
              detailField="medical_examination_details"
              detailPlaceholder="Bitte angeben (Art der Untersuchung/Behandlung)"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
          </div>

          {/* Teil B - Andere Infekte */}
          <div className="questions-list">
            <h3 className="section-questionnaire active">Teil B – Andere Infekte</h3>
            
            <YesNoQuestion
              field="diarrhea"
              question="6. Hatten Sie in den letzten 7 Tagen Durchfall (≥3 ungeformte Stühle pro Tag) für ≥2 Tage?"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
            
            <YesNoQuestion
              field="respiratory_infection"
              question="7. Hatten Sie in den letzten 7 Tagen einen Atemwegsinfekt (Husten, Halsschmerzen, Kurzatmigkeit)?"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
            
            <YesNoQuestion
              field="urinary_infection"
              question="8. Hatten Sie in den letzten 7 Tagen einen Harnwegsinfekt (Brennen beim Wasserlassen, häufiges Wasserlassen)?"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
          </div>

          {/* Teil C - Medikamentenexposition */}
          <div className="questions-list">
            <h3 className="section-questionnaire active">Teil C – Medikamentenexposition (mikrobiomrelevant)</h3>
            
            <YesNoQuestion
              field="antibiotics"
              question="9. Haben Sie in den letzten 7 Tagen Antibiotika eingenommen?"
              detailField="antibiotics_details"
              detailPlaceholder="Präparat und Dauer angeben"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
            
            <YesNoQuestion
              field="ppi"
              question="10. Haben Sie in den letzten 7 Tagen Protonenpumpenhemmer (PPI) eingenommen? (z. B. Omeprazol, Pantoprazol, Esomeprazol)"
              detailField="ppi_details"
              detailPlaceholder="Präparat und Dauer angeben"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
            
            <YesNoQuestion
              field="probiotics"
              question="11. Haben Sie in den letzten 7 Tagen Probiotika oder Prä-/Synbiotika eingenommen?"
              detailField="probiotics_details"
              detailPlaceholder="Präparat und Dauer angeben"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
            
            <YesNoQuestion
              field="hospital_stay"
              question="12. Gab es in den letzten 7 Tagen Krankenhausaufenthalte?"
              detailField="hospital_stay_reason"
              detailPlaceholder="Grund angeben"
              responses={responses}
              onResponseChange={handleResponseChange}
            />
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

          {/* Barre de progression */}
          <div className="questionnaire-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${calculateCompletionRate() * 100}%` }}
              ></div>
            </div>
          </div>

        <div className="questionnaire-footer">
          <button
            className={`submit-btn ${loading || !isComplete() ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={loading || !isComplete()}
          >
            {loading 
              ? 'Wird gesendet...'
              : (isEditing ? 'Aktualisieren' : 'Absenden')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default InfectionQuestionnaire; 